/**
 * Reset Password Page
 *
 * Landing page for password reset links (/reset-password?token=xxx).
 * Validates the token from the URL, collects a new password with confirmation,
 * and calls the ResetPassword GraphQL mutation to complete the flow.
 */

import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { completePasswordRecovery } from '../services/graphql/authService';
import '../styles/shared.css';

const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [isError, setIsError] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!token) {
      setMessage('Invalid reset link. Please request a new one.');
      setIsError(true);
      return;
    }

    if (newPassword.length < 8) {
      setMessage('Password must be at least 8 characters.');
      setIsError(true);
      return;
    }

    if (newPassword !== confirmPassword) {
      setMessage('Passwords do not match.');
      setIsError(true);
      return;
    }

    setLoading(true);
    try {
      const { success, error } = await completePasswordRecovery('', token, newPassword);
      if (error) {
        setMessage(error.message);
        setIsError(true);
      } else if (success) {
        setMessage('Password has been reset successfully. You can now log in.');
        setIsError(false);
      }
    } catch (err: any) {
      setMessage(err.message || 'Failed to reset password.');
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-background text-foreground">
      <section className="w-full max-w-md mx-auto p-6 bg-card rounded-lg shadow-lg">
        <h1 className="text-2xl font-bold mb-6 text-center">Reset Password</h1>

        {!token ? (
          <div className="text-center">
            <p className="text-destructive mb-4">Invalid or missing reset token.</p>
            <button
              onClick={() => navigate('/login')}
              className="text-primary underline"
            >
              Go to Login
            </button>
          </div>
        ) : message && !isError ? (
          <div className="text-center">
            <p className="text-green-600 mb-4">{message}</p>
            <button
              onClick={() => navigate('/login')}
              className="px-4 py-2 bg-primary text-primary-foreground rounded hover:opacity-90"
            >
              Go to Login
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {message && isError && (
              <p className="text-destructive text-sm">{message}</p>
            )}

            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium mb-1">
                New Password
              </label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                className="w-full px-3 py-2 border border-border rounded bg-background text-foreground"
                placeholder="Enter new password"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium mb-1">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                className="w-full px-3 py-2 border border-border rounded bg-background text-foreground"
                placeholder="Confirm new password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-2 bg-primary text-primary-foreground rounded hover:opacity-90 disabled:opacity-50"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        )}
      </section>
    </main>
  );
};

export default ResetPasswordPage;
